import { useMutation } from '@tanstack/react-query';

/* Apis */
import { homeHub } from '@api/apis';

import { StatusErrors } from '@constants/base';
import { deriveResponseStatus } from '@utils/object';
import { Logger } from '@utils/logger';
import useNotice from '@hooks/useNotice';
import { queryClient } from '@integrations/reactquery';
import { homeHubQueryKeys } from '@scenes/HomeHub/store/queries/queries.keys';
import { SURVEY_STATUSES } from '@scenes/HomeHub/homehub.constants';
import { THomeHubState, TSurveyQuestion, TSurveyResponse, TSurveyType } from '@scenes/HomeHub/homehub.types';
import { SegmentTrackingAction, TRACKING_TYPES } from '@integrations/segment/homehub';
import { useUserIdSelector } from '@store/queries/user/useUser.selector';

const mergeQuestionsWithResponse = (cacheQuestions: TSurveyQuestion[], response: TSurveyResponse) =>
  cacheQuestions.map((oldCacheQuestion: TSurveyQuestion) => {
    const foundQuestion = response.survey.questions.find((cacheQuestion: TSurveyQuestion) => cacheQuestion.id === oldCacheQuestion.id);

    return {
      ...oldCacheQuestion,
      ...(foundQuestion && foundQuestion),
    };
  });

/*
  Remove the questions from the flow that are completed as the survey is complete.

  Note: This will mostly be for 'home'. Once a survey is done, we don't want the arrow button
        to allow the user to go back to the survey.
        Note: What about the broswer back button? hmm...
*/
const deleteQuestionsByComplete = (cacheQuestions: TSurveyQuestion[], completedSurveyType: TSurveyType) =>
  cacheQuestions.filter((oldCacheQuestion: TSurveyQuestion) => oldCacheQuestion.surveyType !== completedSurveyType);

/**
 * We have two apps, and setting of the http cookie has issues across the apps.
 * So, we call the "current" on the server side, get the cookie and set it.
 * But to do so, we need to pass along the auth-token.
 *
 * @param {IHash<any> | undefined} options
 * @returns {UseQueryResult<any, unknown>}
 */
type THomeHubUpdate = {
  hid: number;
  seoName: string;
  id: number;
  answers: any;
};

const useHomeMutationUpdateSurvey = () => {
  /* Hooks */
  const { error } = useNotice();
  const userId = useUserIdSelector();

  /* Default Query Options */
  const defaultOptions = {
    retry: 3,
  };

  /* merge default options with passed in options */
  const mergedOptions = { ...defaultOptions };

  return useMutation({
    mutationFn: async (params: THomeHubUpdate) => {
      const { hid, seoName, id, answers } = params;
      const response = await homeHub.updateHomeHubSurvey({ hid, seoName }, { questions: [{ id, answers }] });

      if (StatusErrors.has(deriveResponseStatus(response)) || response.err) throw response;

      return response.data;
    },
    onSuccess: (response, params) => {
      const { survey } = response;
      // get current cache
      const homeHubState = queryClient.getQueryData(homeHubQueryKeys.all) as THomeHubState;
      // get current questions
      const { currentSurvey } = homeHubState;
      const cacheQuestions = currentSurvey?.questions || [];
      const submittedQuestion = cacheQuestions.find((question: TSurveyQuestion) => question.id === params.id);
      // Does our current survey response equate to the surveyType flow?
      // We'd might be taking a home survey, yet be in a security survey flow.
      const isCurrentSurveyType = survey?.seo_name === currentSurvey?.seo_name;
      // Is survey Complete?
      const isCompleteSurveyType = survey?.status === SURVEY_STATUSES.completed ? survey.seo_name : null;
      // merge old cache with new response data
      queryClient.setQueryData(homeHubQueryKeys.all, {
        ...(homeHubState as Record<string, unknown>),
        currentSurvey: {
          ...(isCompleteSurveyType && isCurrentSurveyType ? { ...survey } : { ...currentSurvey }),
          ...(isCurrentSurveyType && { status: survey.status }),
          excepted_questions_ids: survey.excepted_questions_ids,
          questions: isCompleteSurveyType
            ? deleteQuestionsByComplete(cacheQuestions, isCompleteSurveyType)
            : mergeQuestionsWithResponse(cacheQuestions, response),
          /*
              Once we remove the completed survey (home), we still want to actualize the completed count of the "completed" survey.
              Once set, it should be sticky. Refreshing the page should will erase this concern.
          */
          isAnswerCompletedCount: currentSurvey.isAnswerCompletedCount || isCompleteSurveyType ? survey.questions?.length : 0,
        },
      });

      /* -------------- Tracking ---------------- */

      const { user_id, survey_name, question_id, question_name, question_answers } = {
        user_id: userId,
        survey_name: currentSurvey?.seo_name,
        question_id: submittedQuestion?.id,
        question_name: submittedQuestion?.text,
        question_answers: params.answers,
      };

      if (survey?.status === SURVEY_STATUSES.completed) {
        SegmentTrackingAction.homeHubTracking({ user_id, survey_name: survey.seo_name }, TRACKING_TYPES.finishedSurvey);
      }

      SegmentTrackingAction.homeHubTracking(
        { user_id, survey_name, question_id, question_name, question_answers },
        TRACKING_TYPES.surveyQuestionFinished,
      );
    },
    /* We should move this out to the global config and log there */
    onError: err => {
      error('There was an error updating your survey. Try refreshing your page.');
      Logger('Update home hub survey')(err as Error);
    },
    ...mergedOptions,
  });
};

export default useHomeMutationUpdateSurvey;
